import equal from "fast-deep-equal";
import { useMemo } from "react";

import { useDataTableParamState } from "@/common/hooks/useDataTableParamState";
import { getDefaultColumnStates } from "@/common/utils/datatable";
import { GroupingColumn } from "@/feature/components/TableDisplayModePicker";
import {
  FlagHeaderCollectionColumn,
  FlagHeaderCollectionSortBy,
} from "@reflag/shared/flagAPI";
import { FlagViewDTO } from "@reflag/shared/flagViewAPI";

// Column IDs match FlagHeaderCollectionSortBy (not DTO property names)
export const groupingFlagsColumns: GroupingColumn<string>[] = [
  { id: "stage", label: "Stage" },
  { id: "owner", label: "Owner" },
  { id: "autoFeedbackSurveysEnabled", label: "Automated feedback surveys" },
  { id: "lastCheck", label: "Last check" },
  { id: "lastTrack", label: "Last track" },
  { id: "createdAt", label: "Created" },
];

export const groupingStaleFlagsColumns: GroupingColumn<string>[] = [
  { id: "stage", label: "Stage" },
  { id: "owner", label: "Owner" },
  { id: "createdAt", label: "Created" },
];

export function getDefaultFlagsTableColumns(): Array<FlagHeaderCollectionColumn> {
  const columns = [
    "name",
    "key",
    "stage",
    "owner",
    "targetingSummary",
  ] as const satisfies Array<FlagHeaderCollectionColumn>;

  return [...columns];
}

export const defaultStaleFlagViewColumns = [
  "name",
  "key",
  "stage",
  "owner",
  "archivingChecks",
  "cleanup",
  "rolledOutToEveryoneAt",
] as const satisfies Array<FlagHeaderCollectionColumn>;

export const useFlagsTableState = (view: FlagViewDTO) => {
  const initialSorting = useMemo(
    () => [
      {
        id: view.columnSortKey ?? "createdAt",
        desc: view.columnSortDesc ?? true,
      },
    ],
    [view.columnSortKey, view.columnSortDesc],
  );

  const initialColumnStates = useMemo(() => {
    if (!view.columns.length)
      return getDefaultColumnStates(
        view.type === "stale"
          ? defaultStaleFlagViewColumns
          : getDefaultFlagsTableColumns(),
      );
    // Introduce the new column for saved stale views, preserving explicit hiding.
    if (
      view.type === "stale" &&
      !view.columns.some((column) => column.id === "cleanup")
    ) {
      return [...view.columns, { id: "cleanup" as const, shown: true }];
    }
    return view.columns;
  }, [view.columns, view.type]);

  const initialGroupingColumn = useMemo(
    () => (view.type === "stale" ? "createdAt" : "stage"),
    [view.type],
  );

  const allowedGroupingColumns = useMemo(
    () =>
      view.type === "stale"
        ? groupingStaleFlagsColumns.map((col) => col.id)
        : groupingFlagsColumns.map((col) => col.id),
    [view.type],
  );

  // Note: TColumn is string because column IDs (e.g., "lastCheck") differ
  // from DTO property names (e.g., "lastCheckAt")
  const tableState = useDataTableParamState<FlagHeaderCollectionSortBy, string>(
    {
      sorting: initialSorting,
      columnStates: initialColumnStates,
      allowedGroupingColumns,
      groupingColumn: initialGroupingColumn,
    },
  );

  const isDirty =
    !equal(tableState.columnStates, initialColumnStates) ||
    !equal(tableState.sorting, initialSorting);

  return {
    ...tableState,
    isDirty,
  };
};
